import {Box, Grid, Typography} from '@mui/material';

import {ReactComponent as DownloadIcon} from "../../Icons/Download.svg";
import FlagButton from '../FlagButton';
import GamePlatformDialogWithButton from '../Dialog/GamePlatformDialogWithButton'
import NotesDialogWithButton from '../Dialog/NotesDialogWithButton';
import OutlinedButton from "../OutlinedButton";
import makeStyles from '@mui/styles/makeStyles';

import PlayVideoButton from '../Buttons/PlayVideoButton';
import AddToMyListButton from '../Buttons/AddToMyListButton';
import WatchedButton from '../Buttons/WatchedButton';
import { goToDownload } from '../../Utils/Download';
import Metadata from './Metadata';
import { entryIsInMyListFor, entryIsWatchedFor } from '../../Utils/Entry';
import BrokenImage from '../Utils/BrokenImage';

const useStyles = makeStyles(theme => ({
    root: {
        '& .modalLaunchBtn': {
            width:'248px',
            [theme.breakpoints.down('sm')]: {
                width:'335px',
                marginTop: theme.spacing(1),
                marginBottom: theme.spacing(1)
            },
            [theme.breakpoints.down('md')]: {
                width:'335px',
                marginTop: theme.spacing(1),
                marginBottom: theme.spacing(1)
            }

        }
    },
    mediaWrap: {
    },
    mainImage: {
        width: "100%",
        display: "block",
    },
    primaryButtonWrap: {
        marginTop: theme.spacing(2),
    },
    actionArea: {
        display: "flex",
        flexWrap: "wrap",
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1.5),
    },
    actionButtonWrap: {
        [theme.breakpoints.up('sm')]: {
        },
    },
    actionButton: {
        width: "100%",
        [theme.breakpoints.up('sm')]: {
            width: "auto",
        },
    },
    entryTitle: {
        '&:hover': {
            cursor: 'default',
        }
    },
    flags: {
        marginTop: theme.spacing(1.5),
        marginBottom: theme.spacing(1.5),
        '& div':{
            marginRight: theme.spacing(1.2)
        }
    },
    mandatoryFlag: {
        position: "absolute",
        top: '16px',
        left: '16px',
    },
    entryDescription: {
        minHeight: '20px',
        '& a': {
            color: theme.palette.primary.main,

            '&:hover': {
                color: theme.palette.secondary.light
            }
        }
    },
    publishingEndDate: {
        fontWeight: 'bold',
        fontStyle: 'italic',
        marginTop: '0',
        color: theme.palette.secondary.contrastText,
    }
}));

export default function EntrySummary({ award, entry, categoryId = null, content }) {
    const classes = useStyles();

    const entryId = entry.id;
    const entryTitle = entry.title;

    const primaryVideo = entry.data?.primaryEntryVideo;
    const primaryImage = entry.data?.primaryEntryImage;
    const hasNote = entry.hasMemberNote;

    const isInMyList = entryIsInMyListFor(entry, categoryId);
    const isWatched = entryIsWatchedFor(entry, categoryId);

    const publishEndDate = entry?.datePublishingEndString ? new Date(entry?.datePublishingEndString) : null
    const publishEndDateString = publishEndDate
        ? (
            // using undefined as the first param to DateTimeFormat, means the date will be shown in the users' browser locale date format
            // e.g. month first in the US.
            publishEndDate.toLocaleDateString(undefined, {
                dateStyle: 'short',
            })
            + ' at ' +
            publishEndDate.toLocaleTimeString(undefined, {
                hour: '2-digit', 
                minute: '2-digit',
                timeZoneName: 'short',
            })
        )
        : null
    ;

    return (
        <Grid container className={classes.root} spacing={3}>
            <Grid item lg={2} md={3} sm={4} xs={12} className={classes.mediaWrap}>

                {primaryImage && primaryImage.thumbnailUrl ? (
                    <img className={classes.mainImage} src={primaryImage.thumbnailUrl} alt={entry.title+" cover"} />
                ):(
                    <BrokenImage className={`${classes.mainImage}`} />
                )}

                {award.judgingMediaType === "video" && primaryVideo && primaryVideo.answer ? (
                    <div className={classes.primaryButtonWrap}>
                        <PlayVideoButton awardId={award.awardId} entryId={entry.id} mediaId={primaryVideo.answer} style={{
                            width: "100%", 
                        }} />
                    </div>
                ):""}
                {award.judgingMediaType === "game" ? (
                    <div className={classes.primaryButtonWrap}>
                        <GamePlatformDialogWithButton 
                            entryTitle={entryTitle} 
                            awardId={award?.awardId} 
                            entry={entry}
                        />
                    </div>
                ):""}
            </Grid>
            <Grid item lg={10} md={9} sm={8} xs={12} className={classes.entryTitle}>
                <Typography variant="h5" component="h2">
                    {entryTitle}
                </Typography>
                <Metadata entry={entry} style={{marginTop: ".5rem", marginBottom: ".5rem"}} />
                <Box className={classes.flags}>
                    {entry.isMandatory ? (
                        <FlagButton variant="mandatory" />
                    ) : ""}

                    {entry.certification ? (
                        <FlagButton
                            variant={entry.certification.answer}
                        />
                    ) : ""}
                </Box>
                <Box className={classes.entryDescription}>
                    <Typography variant="body1" color="textSecondary" component="p" style={{whiteSpace: 'pre-wrap'}}>
                        {content}
                    </Typography>
                    <br/>
                    {entry.data.vodPlatform ? (
                        <Typography variant="body1" color="textSecondary" component="p">
                            This film is hosted on {entry.data.vodPlatform.answer}. For more information about this VOD service and available viewing option visit <a href="https://help.view.bafta.org/help-articles/vod-platforms/">BAFTA View Help</a> 
                        </Typography>
                    ):''}
                </Box>

                {/* {publishEndDateString && (
                    <p className={`${classes.publishingEndDate} MuiTypography-body1`}>
                        This entry will be removed from BAFTA View on {publishEndDateString}.
                    </p>
                )} */}

                <Grid container className={classes.actionArea} spacing={2}>                    
                    <Grid item className={classes.actionButtonWrap} xs={12} sm={"auto"}>
                        <WatchedButton 
                            awardId={award.awardId}
                            entry={entry}
                            label={award.judgingMediaType === 'video' ? "Watched" : "Played"}
                            isOn={isWatched}
                            className={classes.actionButton}
                            categoryId={award.hasCategoryPage ? categoryId : null}
                        />
                    </Grid>
                    <Grid item className={classes.actionButtonWrap} xs={12} sm={"auto"}>
                        <AddToMyListButton
                            awardId={award.awardId}
                            categoryId={award.hasCategoryPage ? categoryId : null}
                            entry={entry}
                            isOn={isInMyList}
                            className={classes.actionButton}
                        />
                    </Grid>
                    <Grid item className={classes.actionButtonWrap} xs={12} sm={"auto"}>
                        <NotesDialogWithButton 
                            awardId={award.awardId}
                            categoryId={award.hasCategoryPage ? categoryId : null}
                            entryId={entryId}
                            title={entryTitle} 
                            buttonProps={{
                                className: classes.actionButton,
                                isActive: hasNote
                            }} 
                        />
                    </Grid>
                    {primaryVideo?.allowDownload ? (
                        <Grid item className={classes.actionButtonWrap} xs={12} sm={"auto"}>
                            <OutlinedButton icon={DownloadIcon} text="Download"className={classes.actionButton} 
                                onClick={() => goToDownload(award.awardId, entryId, primaryVideo.answer)} underline="none" />
                        </Grid>
                    ):""}
                </Grid>
            </Grid>
        </Grid>
    );
}